import React from 'react'
import { Button, Modal, Title, tokens } from '@arvoreeducacao/bonsai-ds'
import { useTranslation } from 'i18n'
import type { NewReadingGoalModalProps } from './NewReadingGoalModal.types'
import { useNewReadingGoalModal } from './NewReadingGoalModal.hooks'
import HierarchyStep from './hierarchy-step'
import SecondStep from './second-step'
import { NUMBER_OF_STEPS } from './constants'
import ThirdStep from './third-step'
import { Cdn } from 'utils/assets'
import { OTTO_3D } from 'images/images3d'
import { ButtonWrapper } from './first-step/FirstStep.style'
import {
  QuittingDescription,
  StatusOttoWrapper,
  StatusWrapper,
  StepsContent
} from './NewReadingGoalModal.styles'
import EditionSummary from './edition-summary'

const NewReadingGoalModal: React.FC<NewReadingGoalModalProps> = ({
  isOpen,
  onClose,
  entityGoalEditing
}) => {
  const {
    currentStep,
    handlePreviousStep,
    handleNextStep,
    _onClose,
    handleClassSelection,
    selectedClasses,
    setSelectedReadingGoalTime,
    selectedReadingGoalTime,
    isQuitting,
    setIsQuitting,
    successCreatingGoal,
    setSuccessCreatingGoal,
    currentReadingGoals,
    handleSubmit,
    submitting,
    handleClickEditGoal,
    showEditionSummary,
    setShowEdittionSummary,
    setCurrentStep
  } = useNewReadingGoalModal({ onClose, entityGoalEditing })
  const { t } = useTranslation()

  const commonStepProps = {
    handlePreviousStep,
    handleNextStep
  }

  const shouldShowSteps =
    !entityGoalEditing && !isQuitting && !successCreatingGoal

  return (
    <Modal
      isOpen={isOpen}
      onClose={_onClose}
      type={shouldShowSteps ? 'steps' : 'clean'}
      progressIndicatorProps={
        shouldShowSteps && {
          type: 'livros',
          itemsLength: NUMBER_OF_STEPS,
          activeItem: currentStep
        }
      }
      center
      hasDesktopCloseButton
      title={t('Nova meta de leitura')}
      titleSize={3}
      size="xlarge"
      contentDesktopMinHeight={
        shouldShowSteps || entityGoalEditing ? '724px' : undefined
      }
      portalContainer={document.body}
    >
      {showEditionSummary &&
        entityGoalEditing &&
        !isQuitting &&
        !successCreatingGoal && (
          <StepsContent>
            <EditionSummary
              entityGoalEditing={entityGoalEditing}
              handleClickEditGoal={handleClickEditGoal}
            />
          </StepsContent>
        )}
      {isQuitting && (
        <StatusWrapper>
          <StatusOttoWrapper>
            <img src={OTTO_3D.CONFUSED} width="149" />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="117"
              height="112"
              viewBox="0 0 117 112"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M24.6708 19.8004C33.1892 12.2577 41.3941 3.88259 52.5341 1.56833C64.2536 -0.866312 77.3503 -0.0290873 87.169 6.81681C96.6408 13.4209 97.9085 26.3112 102.933 36.7077C108.063 47.3251 117.68 56.5143 116.87 68.2786C116.026 80.5547 107.984 91.3381 98.6244 99.326C89.5274 107.09 77.864 111.393 65.9104 111.769C54.6403 112.124 44.7048 106.349 34.6019 101.342C23.9273 96.0513 11.6253 92.2153 5.65552 81.9052C-0.536199 71.2117 -1.12847 57.6964 2.48916 45.8811C5.9142 34.6948 15.9121 27.5559 24.6708 19.8004Z"
                fill={tokens.colors.primary}
              />
            </svg>
          </StatusOttoWrapper>
          <Title size={3}>{t('Tem certeza que deseja sair?')}</Title>
          <QuittingDescription>
            {t('A atribuição da meta não será salva.')}
          </QuittingDescription>
          <ButtonWrapper>
            <Button
              theme={{ type: 'secondary', size: 'large' }}
              text={t('Voltar')}
              onClick={() => setIsQuitting(false)}
            />
            <Button
              theme={{ type: 'caution', size: 'large' }}
              text={t('Sair')}
              onClick={() => _onClose()}
            />
          </ButtonWrapper>
        </StatusWrapper>
      )}
      {successCreatingGoal && (
        <StatusWrapper>
          <StatusOttoWrapper>
            <img
              src={Cdn.getImageUrl(
                'otto/otto-sorrindo-boca-aberta-e-olhos-fechados@2x.png'
              )}
              width="161"
              style={{
                transform: 'rotate(7.12deg) translateX(-50%) translateY(-50%)',
                top: '50%'
              }}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="129"
              height="122"
              viewBox="0 0 129 122"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M65.4019 -0.00547296C78.2711 0.17106 89.9247 6.20881 100.606 13.3891C111.663 20.8221 123.927 28.835 127.427 41.6901C130.879 54.3688 122.662 66.6322 118.496 79.0946C114.375 91.4248 113.41 105.828 103.269 113.963C92.9682 122.226 78.5805 122.523 65.4019 121.684C52.9025 120.888 41.2896 116.324 30.8829 109.355C20.1108 102.141 10.1534 93.3884 5.23765 81.3918C0.0759689 68.7951 -1.3925 54.4502 3.12275 41.6075C7.58105 28.9269 18.3128 19.8813 29.4892 12.4136C40.2852 5.19999 52.4189 -0.183568 65.4019 -0.00547296Z"
                fill={tokens.colors.primary}
              />
            </svg>
          </StatusOttoWrapper>
          <Title size={3}>{t('Muito bem!')}</Title>
          <QuittingDescription>
            {t('As metas de leitura foram definidas.')}
          </QuittingDescription>
        </StatusWrapper>
      )}
      {shouldShowSteps && (
        <StepsContent>
          {currentStep === 0 && (
            <HierarchyStep
              handleClassSelection={handleClassSelection}
              selectedClasses={selectedClasses}
              {...commonStepProps}
            />
          )}
          {currentStep === 1 && (
            <SecondStep
              handleEditionGoBack={() => {
                setCurrentStep(0)
                setShowEdittionSummary(true)
              }}
              editing={Boolean(entityGoalEditing)}
              originalGoal={entityGoalEditing?.goal}
              setSelectedReadingGoalTime={setSelectedReadingGoalTime}
              submitting={submitting}
              handleSubmit={handleSubmit}
              selectedReadingGoalTime={selectedReadingGoalTime}
              {...commonStepProps}
            />
          )}
          {currentStep === 2 && (
            <ThirdStep
              submitting={submitting}
              handleSubmit={handleSubmit}
              selectedClasses={selectedClasses}
              selectedReadingGoalTime={selectedReadingGoalTime}
              currentReadingGoals={currentReadingGoals}
              onSuccessCreate={() => {
                setSuccessCreatingGoal(true)
              }}
              {...commonStepProps}
            />
          )}
        </StepsContent>
      )}
      {!shouldShowSteps &&
        currentStep === 1 &&
        entityGoalEditing &&
        !isQuitting &&
        !successCreatingGoal && (
          <StepsContent>
            <SecondStep
              handleEditionGoBack={() => {
                setCurrentStep(0)
                setShowEdittionSummary(true)
              }}
              editing={Boolean(entityGoalEditing)}
              originalGoal={entityGoalEditing?.goal}
              setSelectedReadingGoalTime={setSelectedReadingGoalTime}
              submitting={submitting}
              handleSubmit={handleSubmit}
              selectedReadingGoalTime={selectedReadingGoalTime}
              {...commonStepProps}
            />
          </StepsContent>
        )}
    </Modal>
  )
}

export default NewReadingGoalModal
